import { Grid, IconButton, MenuItem, TextField } from '@mui/material';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { filterState } from 'state/exportState';
import { set } from 'object-path';
import { Close } from '@mui/icons-material';
import { FieldTemplate, FormTemplateType } from 'api/models';
import ConditionOperationSelect from 'components/GlobalDialog/AddFieldTemplate/components/ConditionConfig/ConditionOperationSelect';
import {
  ConditionOperation,
  ConditionValueType,
  FieldType,
  ReferenceType,
} from 'api/models/FieldTemplate';
import { getConditionValueType } from 'utils';
import ConditionFieldRenderer from 'components/GlobalDialog/AddFieldTemplate/components/ConditionConfig/ConditionFieldRenderer';
import ConditionFieldAutocomplete from 'components/GlobalDialog/AddFieldTemplate/components/ConditionConfig/ConditionFieldAutocomplete';
import { useState } from 'react';
import StatusSelect from '../StatusSelect';

interface FieldFilterRowProps {
  index: number;
  formTemplates?: FormTemplateType[];
  fields?: FieldTemplate[];
}

export default function FieldFilterRow({
  index,
  formTemplates,
  fields,
}: FieldFilterRowProps) {
  const [filters, setFilters] = useRecoilState(filterState);
  const filter = filters[index];
  const { t } = useTranslation();
  const [formId, setFormId] = useState('');
  const filteredFields = fields?.filter(el => el.formTemplateTypeId === formId);

  const onChange = (value: any) => {
    const updatedFilters = produce(filters, draft => {
      if (draft[index].reference.valueType === ConditionValueType.NUMBER) {
        set(draft[index], 'value', Number(value));
      } else {
        set(draft[index], 'value', value);
      }
    });
    setFilters(updatedFilters);
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={3}>
        <ClearableSelect
          label={t('export.formTemplates')}
          fullWidth
          required
          value={formId}
          error={!formId}
          helperText={
            formId ? '' : t('general.errors.mandatoryField')
          }
          onChange={event => {
            const formId = event.target.value;
            
            setFormId(formId);
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.formTemplateId', formId);
            });

            setFilters(updatedFilters);
          }}
        >
          {formTemplates?.map(formTemplate => (
            <MenuItem key={formTemplate.id} value={formTemplate.id}>
              {formTemplate.name}
            </MenuItem>
          ))}
        </ClearableSelect>
      </Grid>
      <Grid item xs={3}>
        <ConditionFieldAutocomplete
          showMainCategories={false}
          patientFields={[]}
          fieldTemplates={filteredFields ?? []}
          formTemplateTypes={formTemplates ?? []}
          condition={filter}
          onChange={field => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference', {
                ...draft[index].reference,
                fieldTemplateId: field?.id,
                type: ReferenceType.formFieldReference,
                alias: field?.alias,
                attribute: 'value',
                valueType: getConditionValueType(field),
                formTemplateId: formId,
              });
              set(draft[index], 'value', '');
              if (field?.fieldType === FieldType.Select) {
                set(draft[index], 'operation', ConditionOperation.IS_ANY_OF);
              } else {
                set(draft[index], 'operation', ConditionOperation.EQUALS);
              }
            });

            setFilters(updatedFilters);
          }}
        />
      </Grid>
      <Grid item xs={1}>
        <ConditionOperationSelect
          operation={filter.operation}
          onChange={operation => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'operation', operation);
            });
            setFilters(updatedFilters);
          }}
          type={filter?.reference.valueType}
        />
      </Grid>
      <Grid item xs={2.5}>
        <ConditionFieldRenderer
          patientFieldAttribute={filter?.reference.attribute}
          alias={filter?.reference.alias}
          value={filter?.value}
          onChange={onChange}
        />
      </Grid>
      <Grid item xs={1}>
        <StatusSelect
          value={filter.reference.formStatus}
          onChange={statuses => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.formStatus', statuses);
            });
            setFilters(updatedFilters);
          }}
        />
      </Grid>
      <Grid item xs={1}>
        <TextField
          fullWidth
          label={t('export.visitNumber')}
          value={filter.reference.visitIndex}
          onChange={e => {
            const updatedFilters = produce(filters, draft => {
              set(draft[index], 'reference.visitIndex', e.target.value);
            });
            setFilters(updatedFilters);
          }}
        />
      </Grid>
      <Grid item xs={0.5}>
        <IconButton
          sx={{ mt: 2 }}
          onClick={() => {
            const updatedFilters = produce(filters, draft => {
              draft.splice(index, 1);
            });
            setFilters(updatedFilters);
          }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </Grid>
    </Grid>
  );
}
